import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormControl, MenuItem, Select, Grid } from '@mui/material';

import { BarStackHorizontal } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import { thousandFormat, thousandFormat2 } from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  minusActiveColor,
  minusDisableColor,
  thinAxis,
  barThinHeight,
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const finiteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const projectPerformanceNumericFields = [
  'TotalProjectValue',
  'ActualHoursMTD',
  'ActualFeesInvoicedMTD',
  'ActualCostMTD',
  'ActualProfitMTD',
  'ActualHoursYTD',
  'ActualFeesInvoicedYTD',
  'ActualCostYTD',
  'ActualProfitYTD',
  'ActualHoursJTD',
  'ActualFeesInvoicedJTD',
  'ActualCostJTD',
  'ActualProfitJTD',
];

const sanitizeProjectPerformanceRow = (row = {}) => {
  const sanitized = { ...row };
  projectPerformanceNumericFields.forEach((field) => {
    sanitized[field] = finiteNumber(sanitized[field], 0);
  });
  sanitized.ProjectId = sanitized.ProjectId || 'undefined';
  sanitized.ProjectName = sanitized.ProjectName || '';
  sanitized.DirectorInitials = sanitized.DirectorInitials || '';
  sanitized.ProjectTypeDescription = sanitized.ProjectTypeDescription || '';
  sanitized.ProjectSubTypeDescription = sanitized.ProjectSubTypeDescription || '';
  sanitized.ClientName = sanitized.ClientName || '';
  sanitized.ProjectSourceOne = sanitized.ProjectSourceOne || '';
  sanitized.ProjectSourceTwo = sanitized.ProjectSourceTwo || '';
  return sanitized;
};

const getClientGroupKey = (clientName) => `client:${clientName || 'Unspecified Client'}`;

const getProfitValue = (row, profitSubType) => finiteNumber(row[`ActualProfit${profitSubType}`], 0);

const getFeesValue = (row, profitSubType) => finiteNumber(row[`ActualFeesInvoiced${profitSubType}`], 0);

const BottomChart = ({
  detailData,
  selectedTopItems,
  handleFilter,
  showFilter,
  sortFilter,
  profitSubType,
  profitType,
  selectedYears,
}) => {
  const isMounted = useRef(false);
  const chartRootRef = useRef(null);
  const chartBodyRef = useRef(null);
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [chartWidth, setChartWidth] = useState(0);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBarObjs, setSelectedBarObjs] = useState([]);
  const [pageOffset, setPageOffset] = useState(0);
  const [chatData, setChatData] = useState([]);
  const [maxData, setMaxData] = useState(0);
  const [minData, setMinData] = useState(0);
  const [expandedClients, setExpandedClients] = useState({});

  const updateChartWidth = () => {
    if (!isMounted.current) return;

    const rootWidth = chartBodyRef.current
      ? chartBodyRef.current.clientWidth
      : chartRootRef.current
        ? chartRootRef.current.getBoundingClientRect().width - 18
        : window.innerWidth - 18;
    const nextWidth = Math.max(Math.floor(rootWidth || 0), 0);

    setChartWidth((prevWidth) => (prevWidth === nextWidth ? prevWidth : nextWidth));
  };

  const onResize = () => {
    updateChartWidth();
  };

  useEffect(() => {
    isMounted.current = true;
    prepareData();
    updateChartWidth();

    let resizeObserver;
    if (window.ResizeObserver && chartRootRef.current) {
      resizeObserver = new ResizeObserver(updateChartWidth);
      resizeObserver.observe(chartRootRef.current);
    }

    window.addEventListener('resize', onResize);
    return () => {
      isMounted.current = false;
      window.removeEventListener('resize', onResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      prepareData();
    }
  }, [detailData, profitType, profitSubType, selectedTopItems, showFilter, sortFilter, selectedYears]);

  useEffect(() => {
    if (isMounted.current) updateChartWidth();
  }, [chatData.length, expandedClients]);

  const prepareData = () => {
    const isGroupByClient = sortFilter === 'groupbyclient';
    let chat_data = [],
      maxData = 0,
      minData = 0;

    if (detailData) {
      let _item = {};

      detailData.forEach((sourceItem) => {
        const item = sanitizeProjectPerformanceRow(sourceItem);
        _item = item;
        let isInTypeFilter = false,
          isInShowFilter = false;
        if (item.Director != null && item.DirectorInitials != null) {
          if (selectedTopItems)
            selectedTopItems.forEach((filter) => {
              switch (profitType) {
                case 'Director':
                  if (item.Director) {
                    if (item.Director === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.Director === '') isInTypeFilter = true;
                  }
                  break;
                case 'ProjectManager':
                  if (item.ProjectManager) {
                    if (item.ProjectManager === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ProjectManager === '') isInTypeFilter = true;
                  }
                  break;
                case 'Supervisor':
                  if (item.Supervisor) {
                    if (item.Supervisor === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.Supervisor === '') isInTypeFilter = true;
                  }
                  break;
                case 'ProjectTypeDescription':
                  if (item.ProjectTypeDescription) {
                    if (item.ProjectTypeDescription === filter)
                      isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ProjectTypeDescription === '')
                      isInTypeFilter = true;
                  }
                  break;
                case 'ProjectSubTypeDescription':
                  if (item.ProjectSubTypeDescription) {
                    if (item.ProjectSubTypeDescription === filter)
                      isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ProjectSubTypeDescription === '')
                      isInTypeFilter = true;
                  }
                  break;
                case 'ProjectSourceOne':
                  if (item.ProjectSourceOne) {
                    if (item.ProjectSourceOne === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ProjectSourceOne === '') isInTypeFilter = true;
                  }
                  break;
                case 'ProjectSourceTwo':
                  if (item.ProjectSourceTwo) {
                    if (item.ProjectSourceTwo === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ProjectSourceTwo === '') isInTypeFilter = true;
                  }
                  break;
                case 'ClientName':
                  if (item.ClientName) {
                    if (item.ClientName === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ClientName === '') isInTypeFilter = true;
                  }
                  break;
                case 'Project':
                  if (item.ProjectId) {
                    if (item.ProjectId === filter) isInTypeFilter = true;
                  } else if (filter === 'undefined') {
                    if (item.ProjectId === '') isInTypeFilter = true;
                  }
                  break;
                default:
                  isInTypeFilter = true;
              }
            });

          if (!selectedTopItems || selectedTopItems.length === 0)
            isInTypeFilter = true;

          if (isInTypeFilter && isGroupByClient) {
            isInShowFilter = true;
          } else if (isInTypeFilter)
            switch (showFilter) {
              case 'showprofit':
                if (profitSubType === 'YTD') {
                  if (_item.ActualProfitYTD >= 0) {
                    isInShowFilter = true;
                    maxData = Math.max(
                      maxData,
                      item.ActualProfitYTD,
                      item.ActualCostYTD,
                    );
                    minData = Math.min(
                      minData,
                      item.ActualProfitYTD,
                      item.ActualCostYTD,
                    );
                  }
                } else if (profitSubType === 'MTD') {
                  if (_item.ActualProfitMTD >= 0) {
                    isInShowFilter = true;
                    maxData = Math.max(
                      maxData,
                      item.ActualProfitMTD,
                      item.ActualFeesInvoicedMTD,
                    );
                    minData = Math.min(
                      minData,
                      item.ActualProfitMTD,
                      item.ActualFeesInvoicedMTD,
                    );
                  }
                } else {
                  if (_item.ActualProfitJTD >= 0) {
                    isInShowFilter = true;
                    maxData = Math.max(
                      maxData,
                      item.ActualProfitJTD,
                      item.ActualFeesInvoicedJTD,
                    );
                    minData = Math.min(
                      minData,
                      item.ActualProfitJTD,
                      item.ActualFeesInvoicedJTD,
                    );
                  }
                }

                break;
              case 'showloss':
                if (profitSubType === 'YTD') {
                  if (_item.ActualProfitYTD < 0) {
                    isInShowFilter = true;
                    maxData = Math.max(
                      maxData,
                      item.ActualProfitYTD,
                      item.ActualFeesInvoicedYTD,
                    );
                    minData = Math.min(
                      minData,
                      item.ActualProfitYTD,
                      item.ActualFeesInvoicedYTD,
                    );
                  }
                } else if (profitSubType === 'MTD') {
                  if (_item.ActualProfitMTD < 0) {
                    isInShowFilter = true;
                    maxData = Math.max(
                      maxData,
                      item.ActualProfitMTD,
                      item.ActualFeesInvoicedMTD,
                    );
                    minData = Math.min(
                      minData,
                      item.ActualProfitMTD,
                      item.ActualFeesInvoicedMTD,
                    );
                  }
                } else {
                  if (_item.ActualProfitJTD < 0) {
                    isInShowFilter = true;
                    maxData = Math.max(
                      maxData,
                      item.ActualProfitJTD,
                      item.ActualFeesInvoicedJTD,
                    );
                    minData = Math.min(
                      minData,
                      item.ActualProfitJTD,
                      item.ActualFeesInvoicedJTD,
                    );
                  }
                }

                break;
              case 'showall':
                isInShowFilter = true;
                if (profitSubType === 'YTD') {
                  maxData = Math.max(
                    maxData,
                    item.ActualProfitYTD,
                    item.ActualFeesInvoicedYTD,
                  );
                  minData = Math.min(
                    minData,
                    item.ActualProfitYTD,
                    item.ActualFeesInvoicedYTD,
                  );
                } else if (profitSubType === 'MTD') {
                  maxData = Math.max(
                    maxData,
                    item.ActualProfitMTD,
                    item.ActualFeesInvoicedMTD,
                  );
                  minData = Math.min(
                    minData,
                    item.ActualProfitMTD,
                    item.ActualFeesInvoicedMTD,
                  );
                } else {
                  maxData = Math.max(
                    maxData,
                    item.ActualProfitJTD,
                    item.ActualFeesInvoicedJTD,
                  );
                  minData = Math.min(
                    minData,
                    item.ActualProfitJTD,
                    item.ActualFeesInvoicedJTD,
                  );
                }

                break;
              default:
            }

          if (isInTypeFilter && isInShowFilter) {
            if (profitSubType === 'YTD') {
              if (item.FY === selectedYears[0]) chat_data.push(_item);
            } else chat_data.push(_item);
          }
        }
      });
    }

    if (isGroupByClient) {
      const groupedByClient = {};

      chat_data.forEach((projectRow) => {
        const clientName = projectRow.ClientName || 'Unspecified Client';
        const clientKey = getClientGroupKey(clientName);

        if (!groupedByClient[clientKey]) {
          groupedByClient[clientKey] = {
            ProjectId: clientKey,
            ProjectName: '',
            ClientName: clientName,
            ClientKey: clientKey,
            DirectorInitials: '',
            ProjectTypeDescription: '',
            ProjectSubTypeDescription: '',
            FY: projectRow.FY,
            isClientGroup: true,
            childProjects: [],
            projectCount: 0,
            TotalProjectValue: 0,
            ActualHoursMTD: 0,
            ActualFeesInvoicedMTD: 0,
            ActualCostMTD: 0,
            ActualProfitMTD: 0,
            ActualHoursYTD: 0,
            ActualFeesInvoicedYTD: 0,
            ActualCostYTD: 0,
            ActualProfitYTD: 0,
            ActualHoursJTD: 0,
            ActualFeesInvoicedJTD: 0,
            ActualCostJTD: 0,
            ActualProfitJTD: 0,
          };
        }

        const group = groupedByClient[clientKey];
        group.projectCount += 1;
        group.childProjects.push(projectRow);
        projectPerformanceNumericFields.forEach((field) => {
          group[field] += finiteNumber(projectRow[field], 0);
        });
      });

      chat_data = Object.values(groupedByClient).map((group) => {
        group.ProjectTypeDescription = `${group.projectCount} project${group.projectCount === 1 ? '' : 's'}`;
        group.childProjects.sort((a, b) => {
          const profitDifference = getProfitValue(b, profitSubType) - getProfitValue(a, profitSubType);
          if (profitDifference !== 0) return profitDifference;
          return String(a.ProjectName || '').localeCompare(String(b.ProjectName || ''));
        });
        return group;
      });

      chat_data = chat_data.filter((group) => {
        const profitValue = getProfitValue(group, profitSubType);
        if (showFilter === 'showprofit') return profitValue >= 0;
        if (showFilter === 'showloss') return profitValue < 0;
        return true;
      });

      chat_data.forEach((group) => {
        maxData = Math.max(
          maxData,
          getProfitValue(group, profitSubType),
          getFeesValue(group, profitSubType),
        );
        minData = Math.min(
          minData,
          getProfitValue(group, profitSubType),
          getFeesValue(group, profitSubType),
        );
      });

      chat_data.sort((a, b) => {
        const profitDifference = getProfitValue(b, profitSubType) - getProfitValue(a, profitSubType);
        if (profitDifference !== 0) return profitDifference;
        return String(a.ClientName || '').localeCompare(String(b.ClientName || ''));
      });
    } else if (showFilter === 'showloss') {
      if (sortFilter === 'sortbydirector') {
        chat_data.sort((a, b) =>
          a.DirectorInitials.localeCompare(b.DirectorInitials),
        );
      } else if (sortFilter === 'sortbyclient') {
        chat_data.sort((a, b) =>
          String(a.ClientName || '').localeCompare(String(b.ClientName || '')),
        );
      } else {
        if (profitSubType === 'YTD') {
          if (sortFilter === 'sortbyprofit')
            chat_data.sort((a, b) =>
              a.ActualProfitYTD < b.ActualProfitYTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbycosts')
            chat_data.sort((a, b) =>
              a.ActualCostYTD < b.ActualCostYTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbyfees')
            chat_data.sort((a, b) =>
              a.ActualFeesInvoicedYTD < b.ActualFeesInvoicedYTD ? -1 : 1,
            );
        } else if (profitSubType === 'MTD') {
          if (sortFilter === 'sortbyprofit')
            chat_data.sort((a, b) =>
              a.ActualProfitMTD < b.ActualProfitMTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbycosts')
            chat_data.sort((a, b) =>
              a.ActualCostMTD < b.ActualCostMTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbyfees')
            chat_data.sort((a, b) =>
              a.ActualFeesInvoicedMTD < b.ActualFeesInvoicedMTD ? -1 : 1,
            );
        } else {
          if (sortFilter === 'sortbyprofit')
            chat_data.sort((a, b) =>
              a.ActualProfitJTD < b.ActualProfitJTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbycosts')
            chat_data.sort((a, b) =>
              a.ActualCostJTD < b.ActualCostJTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbyfees')
            chat_data.sort((a, b) =>
              a.ActualFeesInvoicedJTD < b.ActualFeesInvoicedJTD ? -1 : 1,
            );
        }
      }
    } else {
      if (sortFilter === 'sortbydirector') {
        chat_data.sort((a, b) =>
          a.DirectorInitials.localeCompare(b.DirectorInitials),
        );
      } else if (sortFilter === 'sortbyclient') {
        chat_data.sort((a, b) =>
          String(a.ClientName || '').localeCompare(String(b.ClientName || '')),
        );
      } else {
        if (profitSubType === 'YTD') {
          if (sortFilter === 'sortbyprofit')
            chat_data.sort((a, b) =>
              a.ActualProfitYTD > b.ActualProfitYTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbycosts')
            chat_data.sort((a, b) =>
              a.ActualCostYTD > b.ActualCostYTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbyfees')
            chat_data.sort((a, b) =>
              a.ActualFeesInvoicedYTD > b.ActualFeesInvoicedYTD ? -1 : 1,
            );
        } else if (profitSubType === 'MTD') {
          if (sortFilter === 'sortbyprofit')
            chat_data.sort((a, b) =>
              a.ActualProfitMTD > b.ActualProfitMTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbycosts')
            chat_data.sort((a, b) =>
              a.ActualCostMTD > b.ActualCostMTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbyfees')
            chat_data.sort((a, b) =>
              a.ActualFeesInvoicedMTD > b.ActualFeesInvoicedMTD ? -1 : 1,
            );
        } else {
          if (sortFilter === 'sortbyprofit')
            chat_data.sort((a, b) =>
              a.ActualProfitJTD > b.ActualProfitJTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbycosts')
            chat_data.sort((a, b) =>
              a.ActualCostJTD > b.ActualCostJTD ? -1 : 1,
            );
          else if (sortFilter === 'sortbyfees')
            chat_data.sort((a, b) =>
              a.ActualFeesInvoicedJTD > b.ActualFeesInvoicedJTD ? -1 : 1,
            );
        }
      }
    }

    setChatData(chat_data);
    setMaxData(maxData);
    setMinData(minData);
  };

  const handleFilterChange = (event) => {
    handleFilter({
      [event.target.name]: event.target.value,
    });
  };

  const getColor = (bar) => {
    let activeColor = '#27aae1';
    let disableColor = '#1c75bc';
    if (bar.key.includes('Invoiced')) {
      if (bar.bar.data['ActualFeesInvoiced' + profitSubType] !== undefined) {
        if (bar.bar.data['ActualFeesInvoiced' + profitSubType] < 0) {
          activeColor = minusActiveColor;
          disableColor = minusDisableColor;
        } else {
          activeColor = negativeActiveColor;
          disableColor = negativeDisableColor;
        }
      }
    } else {
      if (bar.bar.data['ActualProfit' + profitSubType] !== undefined) {
        if (bar.bar.data['ActualProfit' + profitSubType] < 0) {
          activeColor = minusActiveColor;
          disableColor = minusDisableColor;
        } else {
          activeColor = positiveActiveColor;
          disableColor = positiveDisableColor;
        }
      }
    }

    let index = -1;
    if (selectedBarObjs && selectedBarObjs.length !== 0) {
      selectedBarObjs.forEach((item) => {
        if (item.ProjectId === bar.bar.data.ProjectId) {
          index++;
        }
      });
    } else {
      return activeColor;
    }

    if (index >= 0) return activeColor;

    return disableColor;
  };

  const getShortName = (name) => {
    let sortname = '';
    if (name) {
      for (let i = 0; i < name.length; i++) {
        if (name[i] >= 'A' && name[i] <= 'Z') sortname += name[i];
      }
    }
    return sortname;
  };

  const handleBar = (event, bar) => {
    event.stopPropagation();
    let _selectedBarObjs;

    if (event.shiftKey) {
      _selectedBarObjs = selectedBarObjs.slice();

      let index = NaN;
      for (let i = 0; i < selectedBarObjs.length; i++) {
        if (selectedBarObjs[i].ProjectId === bar.bar.data.ProjectId) {
          index = i;
        }
      }

      if (isNaN(index)) {
        _selectedBarObjs.push({ ...bar.bar.data });
      } else {
        _selectedBarObjs.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBarObjs.forEach((barObj) => {
        if (barObj.ProjectId === bar.bar.data.ProjectId) exist = true;
      });

      if (exist && selectedBarObjs.length === 1) {
        _selectedBarObjs = [];
      } else {
        _selectedBarObjs = [bar.bar.data];
      }
    }

    setSelectedBarObjs(_selectedBarObjs);
    handleFilter({
      selectedBarObjs: _selectedBarObjs,
    });
  };

  const handleClickRow = (event, item) => {
    if (item.isClientGroup) return;

    if (event.shiftKey) {
      let _selectedBarObjs = selectedBarObjs.slice();
      _selectedBarObjs = _selectedBarObjs.filter(
        (barObj) => barObj.ProjectId !== item.ProjectId,
      );

      let data = {
        ProjectId: item.ProjectId,
        ProjectName: item.ProjectName,
        ClientName: item.ClientName,
        ProjectTypeDescription: item.ProjectTypeDescription,
        TotalProjectValue: item.TotalProjectValue,
        ActualHoursMTD: item.ActualHoursMTD,
        ActualFeesInvoicedMTD: item.ActualFeesInvoicedMTD,
        ActualCostMTD: item.ActualCostMTD,
        ActualProfitMTD: item.ActualProfitMTD,
        ActualHoursYTD: item.ActualHoursYTD,
        ActualFeesInvoicedYTD: item.ActualFeesInvoicedYTD,
        ActualCostYTD: item.ActualCostYTD,
        ActualProfitYTD: item.ActualProfitYTD,
        ActualHoursJTD: item.ActualHoursJTD,
        ActualFeesInvoicedJTD: item.ActualFeesInvoicedJTD,
        ActualCostJTD: item.ActualCostJTD,
        ActualProfitJTD: item.ActualProfitJTD,
      };
      _selectedBarObjs.push(data);
      setSelectedBarObjs(_selectedBarObjs);
    }
  };

  const toggleClientGroup = (event, clientKey) => {
    event.stopPropagation();
    setExpandedClients((prevExpandedClients) => ({
      ...prevExpandedClients,
      [clientKey]: !prevExpandedClients[clientKey],
    }));
  };

  const deSelectAll = () => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);
    setSelectedBarObjs([]);
    handleFilter({
      selectedBarObjs: [],
    });
  };

  const showTooltipHandler = (event, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let index = -1;
    if (selectedBarObjs && selectedBarObjs.length !== 0) {
      selectedBarObjs.forEach((item) => {
        if (item.ProjectId === bar.bar.data.ProjectId) {
          index++;
        }
      });
    }

    let data = {},
      top,
      left,
      offset = 50;

    top = event.clientY - pageOffset - offset;
    left =
      window.innerWidth / 2 < event.clientX
        ? event.clientX - 360
        : event.clientX + 30;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      if (index > -1 && selectedBarObjs.length > 1) {
        data = {
          ProjectTypeDescription: '',
          TotalProjectValue: 0,
          ActualHoursMTD: 0,
          ActualFeesInvoicedMTD: 0,
          ActualCostMTD: 0,
          ActualProfitMTD: 0,
          ActualHoursYTD: 0,
          ActualFeesInvoicedYTD: 0,
          ActualCostYTD: 0,
          ActualProfitYTD: 0,
          ActualHoursJTD: 0,
          ActualFeesInvoicedJTD: 0,
          ActualCostJTD: 0,
          ActualProfitJTD: 0,
        };
        let existProjects = [];
        selectedBarObjs.forEach((item, index) => {
          data.ProjectTypeDescription +=
            (index === 0 ? '' : ', ') + item.ProjectTypeDescription;
          data.TotalProjectValue += item.TotalProjectValue;
          data.ActualHoursMTD += item.ActualHoursMTD;
          data.ActualHoursYTD += item.ActualHoursYTD;
          data.ActualHoursJTD += item.ActualHoursJTD;
          data.ActualFeesInvoicedMTD += item.ActualFeesInvoicedMTD;
          data.ActualFeesInvoicedYTD += item.ActualFeesInvoicedYTD;
          data.ActualFeesInvoicedJTD += item.ActualFeesInvoicedJTD;
          data.ActualProfitMTD += item.ActualProfitMTD;
          data.ActualProfitYTD += item.ActualProfitYTD;
          data.ActualProfitJTD += item.ActualProfitJTD;
          data.ActualCostMTD += item.ActualCostMTD;
          data.ActualCostYTD += item.ActualCostYTD;
          data.ActualCostJTD += item.ActualCostJTD;
          existProjects.push(data.ProjectId);
        });
      } else {
        data = bar.bar.data;
      }
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  };

  const hideTooltipHandler = () => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  };

  const handlePos = (event) => {
    const _pos =
      event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (pageOffset !== _pos && isMounted.current === true) {
      setPageOffset(_pos);
    }
  };

  const fallbackWidth = window.innerWidth - 18 > 0 ? window.innerWidth - 18 : 0;
  const width = chartWidth || fallbackWidth;
  const isGroupedByClient = sortFilter === 'groupbyclient';
  const isSortedByClient = sortFilter === 'sortbyclient';
  const showsClientColumn = isSortedByClient || isGroupedByClient;
  const displayData = isGroupedByClient
    ? chatData.reduce((rows, clientGroup) => {
        rows.push(clientGroup);
        if (expandedClients[clientGroup.ClientKey]) {
          clientGroup.childProjects.forEach((project) => {
            rows.push({
              ...project,
              ProjectId: `${clientGroup.ClientKey}:${project.ProjectId}`,
              OriginalProjectId: project.ProjectId,
              ParentClientKey: clientGroup.ClientKey,
              isClientChild: true,
            });
          });
        }
        return rows;
      }, [])
    : chatData;
  const ySelector = (item) => item.ProjectId;

  let numTicks = 10;
  if (maxData === 0 && minData === 0) {
    numTicks = 2;
  }

  let keys_active = [];
  let keys_invoiced = [];
  keys_invoiced.push('ActualFeesInvoiced' + profitSubType);
  keys_active.push('ActualProfit' + profitSubType);

  const color = scaleOrdinal({
    domain: keys_invoiced,
    range: ['#191919', '#FFCF02'],
  });

  let projectNameLengths = [],
    maxProjectNameLength = 0,
    clientNameLengths = [],
    maxClientNameLength = 6,
    directorLengths = [],
    projectTypeDescriptionLengths = [],
    maxProjectTypeDescriptionLength = 7;
  displayData.forEach((d) => {
    projectNameLengths.push(d.ProjectName.length);
    clientNameLengths.push(d.ClientName ? d.ClientName.length : 0);
    directorLengths.push(d.DirectorInitials.length);
    projectTypeDescriptionLengths.push(
      d.ProjectTypeDescription ? d.ProjectTypeDescription.length : 0,
    );
  });
  if (projectNameLengths.length > 0) {
    maxProjectNameLength = Math.max(...projectNameLengths);
    maxClientNameLength = Math.max(...clientNameLengths, maxClientNameLength);
    maxProjectTypeDescriptionLength = Math.max(
      ...projectTypeDescriptionLengths,
    );
  }

  const maxClientColumnWidth = Math.max(Math.min(width * 0.18, 230), 120);
  const maxNameColumnWidth = Math.max(
    Math.min(width * (showsClientColumn ? 0.26 : 0.34), showsClientColumn ? 310 : 380),
    showsClientColumn ? 150 : 180,
  );
  const maxProjectTypeColumnWidth = Math.max(Math.min(width * 0.16, 180), 80);
  const clientNameWidth = showsClientColumn
    ? Math.min(maxClientNameLength * 7.1, maxClientColumnWidth)
    : 0;
  const projectNameWidth = Math.min(maxProjectNameLength * 7.1, maxNameColumnWidth);
  const projectTypeWidth = Math.min(maxProjectTypeDescriptionLength * 7.6, maxProjectTypeColumnWidth);

  let axisLeftWidth =
    clientNameWidth +
    projectNameWidth +
    28 +
    projectTypeWidth -
    1;

  const rowChartWidth = Math.max(width - axisLeftWidth - 1, 0);
  const xMax = Math.max(rowChartWidth - 24, 1);

  // scales
  const yScale = scaleBand()
    .rangeRound([5, 0])
    .domain(displayData.map(ySelector))
    .padding(0.2);

  const displayMaxData = isGroupedByClient
    ? displayData.reduce(
        (maxValue, item) => Math.max(maxValue, getProfitValue(item, profitSubType), getFeesValue(item, profitSubType)),
        0,
      )
    : maxData;
  const displayMinData = isGroupedByClient
    ? displayData.reduce(
        (minValue, item) => Math.min(minValue, getProfitValue(item, profitSubType), getFeesValue(item, profitSubType)),
        0,
      )
    : minData;
  const safeMinData = finiteNumber(displayMinData, 0);
  const safeMaxData = finiteNumber(displayMaxData, 0);
  const xDomain =
    safeMinData === safeMaxData
      ? [safeMinData - 1, safeMaxData + 1]
      : [safeMinData * 1.01, safeMaxData * 1.01];

  const xScale = scaleLinear({
    range: [0, xMax],
    domain: xDomain,
  });

  return (
    <div
      ref={chartRootRef}
      style={{
        minHeight: '64vh',
      }}
      id="bottomRoot"
      onMouseMove={handlePos}
      onTouchMove={handlePos}
    >
      <div>
        <div
          className="subtitle mb-10"
          style={{ marginTop: 18, userSelect: 'none' }}
        >
          Project performance
        </div>
      </div>

      <div className="wrapper" style={{ padding: '5px 0' }}>
        <FormControl variant="standard">
          <Select
            value={showFilter}
            name="showFilter"
            sx={{
              '& .MuiSelect-select': {
                padding: 0,
                paddingRight: '24px',
                fontWeight: 'bold !important',
                fontFamily: 'Verdana !important',
                fontSize: '10pt !important',
              },
            }}
            onChange={handleFilterChange}
          >
            <MenuItem value="showprofit">Show Profit</MenuItem>
            <MenuItem value="showloss">Show Loss</MenuItem>
            <MenuItem value="showall">Show All</MenuItem>
          </Select>
        </FormControl>
        <div className="">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
        <FormControl variant="standard">
          <Select
            sx={{
              '& .MuiSelect-select': {
                padding: 0,
                paddingRight: '24px',
                fontWeight: 'bold !important',
                fontFamily: 'Verdana !important',
                fontSize: '10pt !important',
              },
            }}
            value={sortFilter}
            name="sortFilter"
            onChange={handleFilterChange}
          >
            <MenuItem value="sortbyprofit">Sort by Profit</MenuItem>
            <MenuItem value="sortbyfees">Sort by Fees</MenuItem>
            <MenuItem value="sortbycosts">Sort by Costs</MenuItem>
            <MenuItem value="sortbydirector">Sort by Director</MenuItem>
            <MenuItem value="sortbyclient">Sort by Client</MenuItem>
            <MenuItem value="groupbyclient">Group by Client</MenuItem>
          </Select>
        </FormControl>
        <div className="">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
        <FormControl variant="standard">
          <Select
            sx={{
              '& .MuiSelect-select': {
                padding: 0,
                paddingRight: '24px',
                fontWeight: 'bold !important',
                fontFamily: 'Verdana !important',
                fontSize: '10pt !important',
              },
            }}
            value={profitSubType}
            name="profitSubType"
            onChange={handleFilterChange}
          >
            <MenuItem value="YTD">YTD</MenuItem>
            <MenuItem value="MTD">MTD</MenuItem>
            <MenuItem value="JTD">PTD</MenuItem>
          </Select>
        </FormControl>
      </div>

      {chatData.length > 0 ? (
        <>
          <div
            style={{
              maxHeight: window.innerWidth >= 1280 ? '55.4vh' : '432px',
              overflowY: 'auto',
              overflowX: 'hidden',
              fontSize: 12,
              width: '100%',
            }}
            id="pp_bottom"
            ref={chartBodyRef}
          >
            {displayData.map((item, i) => {
              const yMax = barThinHeight - 7;

              return (
                <Grid
                  container
                  wrap="nowrap"
                  key={i}
                  sx={{
                    borderTop: 'solid 1px #a9a9a9',
                    flexWrap: 'nowrap',
                    width: '100%',
                    minWidth: 0,
                    backgroundColor: item.isClientGroup ? '#f5f5f5' : 'transparent',
                  }}
                  onClick={(event) => handleClickRow(event, item)}
                >
                  {showsClientColumn && (
                    <div
                      style={{
                        borderRight: 'solid 1px #a9a9a9',
                        height: 23,
                        display: 'flex',
                        alignItems: 'center',
                        width: clientNameWidth,
                        flex: `0 0 ${clientNameWidth}px`,
                        minWidth: 0,
                        paddingRight: 6,
                        userSelect: 'none',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                      title={item.ClientName || ''}
                    >
                      {isGroupedByClient && item.isClientGroup && (
                        <button
                          type="button"
                          onClick={(event) => toggleClientGroup(event, item.ClientKey)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 11,
                            lineHeight: '14px',
                            padding: '0 3px 0 2px',
                            userSelect: 'none',
                          }}
                          title={expandedClients[item.ClientKey] ? 'Hide projects' : 'Show projects'}
                        >
                          {expandedClients[item.ClientKey] ? '▾' : '▸'}
                        </button>
                      )}
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          width: '100%',
                          fontWeight: item.isClientGroup ? 'bold' : 'normal',
                          paddingLeft: isGroupedByClient && item.isClientChild ? 17 : 0,
                        }}
                      >
                        &nbsp;{item.isClientChild ? '' : item.ClientName ? item.ClientName : ''}
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      borderRight: 'solid 1px #a9a9a9',
                      height: 23,
                      display: 'flex',
                      alignItems: 'center',
                      width: projectNameWidth,
                      flex: `0 0 ${projectNameWidth}px`,
                      minWidth: 0,
                      paddingRight: 6,
                      userSelect: 'none',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      fontWeight: item.isClientGroup ? 'bold' : 'normal',
                    }}
                    title={item.isClientGroup ? 'Client total' : item.ProjectName || ''}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        width: '100%',
                        paddingLeft: item.isClientChild ? 16 : 0,
                      }}
                    >
                      &nbsp;{item.isClientGroup ? 'Client total' : item.ProjectName ? item.ProjectName : ''}
                    </span>
                  </div>
                  <div
                    style={{
                      borderRight: 'solid 1px #a9a9a9',
                      height: 23,
                      display: 'flex',
                      alignItems: 'center',
                      width: 28,
                      flex: '0 0 28px',
                      minWidth: 28,
                      userSelect: 'none',
                      justifyContent: 'center',
                    }}
                  >
                    {item.DirectorInitials ? item.DirectorInitials : ''}
                  </div>
                  <div
                    style={{
                      borderRight: 'solid 1px #a9a9a9',
                      height: 23,
                      display: 'flex',
                      alignItems: 'center',
                      width: projectTypeWidth,
                      flex: `0 0 ${projectTypeWidth}px`,
                      minWidth: 0,
                      paddingRight: 6,
                      userSelect: 'none',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    &nbsp;
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}>
                      {item.ProjectTypeDescription
                        ? item.ProjectTypeDescription
                        : ''}
                    </span>
                  </div>

                  <div
                    style={{
                      position: 'relative',
                      height: 23,
                      width: rowChartWidth,
                      flex: `1 1 ${rowChartWidth}px`,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <svg
                      width={rowChartWidth}
                      height={23}
                    >
                      <rect
                        x={0}
                        y={0}
                        width={rowChartWidth}
                        height={23}
                        fill="transparent"
                        onClick={deSelectAll}
                      />
                      <BarStackHorizontal
                        data={[item]}
                        keys={keys_invoiced}
                        y={ySelector}
                        xScale={xScale}
                        yScale={yScale}
                        color={color}
                      >
                        {(barStacks) => {
                          return barStacks.map((barStack) => {
                            return barStack.bars.map((bar) => {
                              if (bar.width < 0) {
                                bar.x = xScale(0) + bar.width;
                                bar.width = Math.abs(bar.width);
                              } else if (
                                bar.width === 0 ||
                                isNaN(bar.width)
                              ) {
                                bar.width = 1;
                              }

                              return (
                                <rect
                                  key={`barstack-horizontal-${barStack.index}-${bar.index}`}
                                  x={finiteNumber(xScale(0), 0)}
                                  y={2.5}
                                  height={finiteNumber(yMax, 1)}
                                  width={finiteNumber(bar.width, 0)}
                                  className={
                                    selectedBarObjs.find(
                                      (item) =>
                                        item.ProjectId ===
                                        bar.bar.data.ProjectId,
                                    ) && 'barActive'
                                  }
                                  fill={getColor(bar)}
                                  onClick={(event) =>
                                    handleBar(event, bar)
                                  }
                                  onMouseLeave={hideTooltipHandler}
                                  onMouseMove={(event) =>
                                    showTooltipHandler(event, bar)
                                  }
                                  onTouchEnd={hideTooltipHandler}
                                  onTouchMove={(event) =>
                                    showTooltipHandler(event, bar)
                                  }
                                />
                              );
                            });
                          });
                        }}
                      </BarStackHorizontal>
                      <BarStackHorizontal
                        data={[item]}
                        keys={keys_active}
                        y={ySelector}
                        xScale={xScale}
                        yScale={yScale}
                        color={color}
                      >
                        {(barStacks) => {
                          return barStacks.map((barStack) => {
                            return barStack.bars.map((bar) => {
                              if (bar.width < 0) {
                                bar.x = xScale(0) + bar.width;
                                bar.width = Math.abs(bar.width);
                              } else if (
                                bar.width === 0 ||
                                isNaN(bar.width)
                              ) {
                                bar.width = 1;
                              }

                              return (
                                <rect
                                  key={`barstack-horizontal-${barStack.index}-${bar.index}`}
                                  x={finiteNumber(bar.x, 0)}
                                  y={2.5}
                                  height={finiteNumber(yMax, 1)}
                                  width={finiteNumber(bar.width, 0)}
                                  className={
                                    selectedBarObjs.find(
                                      (item) =>
                                        item.ProjectId ===
                                        bar.bar.data.ProjectId,
                                    ) && 'barActive'
                                  }
                                  fill={getColor(bar)}
                                  onClick={(event) =>
                                    handleBar(event, bar)
                                  }
                                  onMouseLeave={hideTooltipHandler}
                                  onMouseMove={(event) =>
                                    showTooltipHandler(event, bar)
                                  }
                                  onTouchEnd={hideTooltipHandler}
                                  onTouchMove={(event) =>
                                    showTooltipHandler(event, bar)
                                  }
                                />
                              );
                            });
                          });
                        }}
                      </BarStackHorizontal>
                    </svg>
                  </div>
                </Grid>
              );
            })}
          </div>

          <div
            style={{
              height: barThinHeight + 4,
              borderTop: 'solid 1px #a9a9a9',
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative',
              userSelect: 'none',
            }}
            onClick={deSelectAll}
          >
            <div style={{ position: 'absolute', top: 5 }}>
              <svg height={20} width={axisLeftWidth}>
                <Group top={0} left={0}>
                  <rect x={0} y={0} width={12} height={12} fill="#919698" />
                  <text x={14} y={12} fontSize={12}>
                    Invoiced
                  </text>
                </Group>
                <Group top={0} left={0}>
                  <rect x={100} y={0} width={12} height={12} fill="#8dc63f" />
                  <text x={114} y={12} fontSize={12}>
                    Profit
                  </text>
                </Group>
                <Group top={0} left={0}>
                  <rect x={200} y={0} width={12} height={12} fill="#be1e2d" />
                  <text x={214} y={12} fontSize={12}>
                    Loss
                  </text>
                </Group>
              </svg>
            </div>
            <svg width={width} height={barThinHeight}>
              <rect
                x={0}
                y={0}
                width={width}
                height={barThinHeight}
                fill="transparent"
              />
              <AxisBottom
                scale={xScale}
                top={0}
                left={axisLeftWidth}
                hideAxisLine={true}
                stroke="black"
                numTicks={numTicks}
                tickStroke="#a9a9a9"
                tickFormat={(value, index) => value / 1000}
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '0.2em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps}>
                    $
                    {Math.abs(formattedValue) < 1000
                      ? `${formattedValue}K`
                      : `${formattedValue / 1000}M`}
                  </text>
                )}
              />
            </svg>
          </div>
        </>
      ) : (
        <div style={thinAxis} onClick={deSelectAll} align="center">
          <p>No Data</p>
        </div>
      )}
      {tooltipOpen && (
        <TooltipWithBounds
         
          left={tooltipLeft}
          top={tooltipTop}
        >
          {tooltipData.isBar ? (
            <div className="bkg-white color-black" style={{ width: 300, userSelect: 'none' }}>
              {tooltipData.isClientGroup && (
                <div className="pdv-10">
                  Client:&nbsp;&nbsp;
                  <strong>{tooltipData.ClientName}</strong>
                </div>
              )}
              <div className="pdv-10">
                {tooltipData.isClientGroup ? 'Projects:' : 'Project Type:'}&nbsp;&nbsp;
                <strong>{tooltipData.ProjectTypeDescription}</strong>
              </div>
              <div className="pdv-10">
                Project Value:&nbsp;&nbsp;
                <strong>
                  {' '}
                  {thousandFormat2(tooltipData.TotalProjectValue)}
                </strong>
              </div>
              <Grid container>
                <Grid size="grow">
                  <div>
                    <p>&nbsp;&nbsp;&nbsp;&nbsp;</p>
                    <p>
                      <strong>Hours</strong>
                    </p>
                    <p>
                      <strong>Invoiced</strong>
                    </p>
                    <p>
                      <strong>Cost</strong>
                    </p>
                    <p>
                      <strong>Profit</strong>
                    </p>
                  </div>
                </Grid>
                <Grid size="grow">
                  <div>
                    <p>
                      <strong>Month</strong>
                    </p>
                    <p>{thousandFormat(tooltipData.ActualHoursMTD)}</p>
                    <p>
                      {thousandFormat2(tooltipData.ActualFeesInvoicedMTD)}
                    </p>
                    <p>{thousandFormat2(tooltipData.ActualCostMTD)}</p>
                    <p>{thousandFormat2(tooltipData.ActualProfitMTD)}</p>
                  </div>
                </Grid>
                <Grid size="grow">
                  <div>
                    <p>
                      <strong>YTD</strong>
                    </p>
                    <p>{thousandFormat(tooltipData.ActualHoursYTD)}</p>
                    <p>
                      {thousandFormat2(tooltipData.ActualFeesInvoicedYTD)}
                    </p>
                    <p>{thousandFormat2(tooltipData.ActualCostYTD)}</p>
                    <p>{thousandFormat2(tooltipData.ActualProfitYTD)}</p>
                  </div>
                </Grid>
                <Grid size="grow">
                  <div>
                    <p>
                      <strong>PTD</strong>
                    </p>
                    <p>{thousandFormat(tooltipData.ActualHoursJTD)}</p>
                    <p>
                      {thousandFormat2(tooltipData.ActualFeesInvoicedJTD)}
                    </p>
                    <p>{thousandFormat2(tooltipData.ActualCostJTD)}</p>
                    <p>{thousandFormat2(tooltipData.ActualProfitJTD)}</p>
                  </div>
                </Grid>
              </Grid>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  handleFilter: PropTypes.func.isRequired,
  showFilter: PropTypes.string.isRequired,
  sortFilter: PropTypes.string.isRequired,
  profitSubType: PropTypes.string.isRequired,
  profitType: PropTypes.string.isRequired,
  selectedYears: PropTypes.array.isRequired,
};

export default BottomChart;
